import { useEffect, useState } from 'react'
import { datadogLogs, LogsEvent } from '@datadog/browser-logs'
import { useRoles, useUserProfile } from 'core/hooks/data/useDataHook'
import useCurrentTeam from 'core/hooks/team/useCurrentTeam'

const IGNORED_MESSAGE_PATTERNS = [
  /AxiosError/,
  /Ada Embed has already been started\./,
  /https:\/\/px\.ads\.linkedin\.com\/wa\//,
  /https:\/\/[^/]*\.braze\.com\/api\//,
  /https:\/\/api-js\.mixpanel\.com\//,
  /https:\/\/[^/]*\.clarity\.ms\//
]

const shouldIgnoreLog = (message: string) =>
  IGNORED_MESSAGE_PATTERNS.some((pattern) => pattern.test(message))

const beforeSend = (event: LogsEvent) => {
  return !shouldIgnoreLog(event.message)
}

const useInitDataDog = () => {
  const { data: user } = useUserProfile()
  const { data: userRoles } = useRoles()
  const { data: currentTeam } = useCurrentTeam()
  const [hasClearedUser, setHasClearedUser] = useState(false)

  useEffect(() => {
    if (process.env.NODE_ENV === 'production') {
      datadogLogs.init({
        clientToken: import.meta.env.VITE_APP_DATADOG_CLIENT_TOKEN_KEY,
        site: 'datadoghq.com',
        forwardErrorsToLogs: true,
        sessionSampleRate: 100,
        service: 'everlance-dashboard',
        beforeSend
      })
    }
  }, [])

  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') return

    const hasUser = !!datadogLogs.getUser()?.email
    if (user?.token_id && !hasUser) {
      datadogLogs.setUser({
        id: user.token_id as string,
        email: user.email,
        roles: userRoles?.map((role) => role.name),
        teamId: currentTeam?.token_id
      })
    }

    if (!user?.token_id && hasUser && !hasClearedUser) {
      datadogLogs.clearUser()
      setHasClearedUser(true)
    }
  }, [
    currentTeam?.token_id,
    hasClearedUser,
    user?.email,
    user?.token_id,
    userRoles
  ])

  return null
}

export default useInitDataDog
